import { Link } from "react-router-dom";
import { ArrowUpRight, Sun, Droplets, FlaskConical, Sprout, Briefcase, Beaker, Shield } from "lucide-react";
import { Reveal } from "@/hooks/useInView";

const FARM_IMG = "https://images.unsplash.com/photo-1513377498349-009a3ca9f79c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxzcGlydWxpbmElMjBmYXJtJTIwYWVyaWFsfGVufDB8fHx8MTc3ODc3NTU2Nnww&ixlib=rb-4.1.0&q=85";

const FOUNDERS = [
  {
    initials: "MA",
    name: "Madhu Babu Alegula",
    title: "Co-Founder & Chief Executive Officer",
    role: "Commercial Leadership",
    accent: "#c9a84c",
    accentClass: "text-brand-gold border-brand-gold/40 bg-brand-gold/10",
    initialBg: "bg-brand-gold/15 text-[#8a6e1f]",
    icon: Briefcase,
    chips: ["Osmania University Graduate", "Healthcare Industry Veteran", "Serial Entrepreneur"],
    bio: "Madhu brings over 15 years of proven entrepreneurial experience building and scaling organisations in the healthcare sector — multiple ventures grown to 1,000+ member teams from the ground up. His deep familiarity with India's nutraceutical and pharmaceutical buyer landscape, combined with strong institutional relationships with Telangana government bodies (T-IDEA, TSIIC), forms the commercial backbone of Spiruva. At Spiruva, Madhu leads market development, banking relationships, export partnerships, and investor relations.",
  },
  {
    initials: "SR",
    name: "Sripal Reddy Molugu",
    title: "Co-Founder & Chief Technology Officer",
    role: "Technical Architecture",
    accent: "#006d77",
    accentClass: "text-brand-teal border-brand-teal/40 bg-brand-teal/10",
    initialBg: "bg-brand-teal/15 text-brand-teal",
    icon: Beaker,
    chips: ["B.Tech Chemical Engineering, IIT Kharagpur", "Oil & Gas Industry CEO", "Process Scale-Up Expert"],
    bio: "Sripal is the technical architect behind Spiruva's phycocyanin extraction and purification systems. His IIT Kharagpur Chemical Engineering degree and career managing large-scale continuous process operations in the oil & gas sector make him uniquely qualified to design, commission, and optimise both food-grade (E18) and pharmaceutical-grade (E30) extraction lines. He leads all technology development, equipment procurement, BIRAC grant applications, and research partnerships with IIT Kharagpur and CSIR-IICT Hyderabad.",
  },
  {
    initials: "GR",
    name: "Ganga Reddy Kallem",
    title: "Co-Founder & Chief Operations Officer",
    role: "Operations & Governance",
    accent: "#1a4d2e",
    accentClass: "text-brand-green border-brand-green/40 bg-brand-green/10",
    initialBg: "bg-brand-green/15 text-brand-green",
    icon: Shield,
    chips: ["Retired Superintendent of Police, Telangana", "35 Years Government Service", "Floriculture & Protected Cultivation Pioneer"],
    bio: "Ganga Reddy brings an unmatched combination of operational discipline and agricultural expertise to Spiruva. After 35 years of distinguished service with the Telangana State Police — including counter-terrorism operations and CID leadership — he channelled that same precision into pioneering greenhouse floriculture and protected cultivation in the region. His deep-rooted knowledge of Nizamabad's agricultural community informed Spiruva's site selection, and his command of Telangana's regulatory environment ensures facility operations run with police-grade discipline. He oversees site operations, certifications, government compliance, and sustainable farming practices.",
  },
];

const WHY_INDIA = [
  { icon: Sun, t: "300+ Cultivation Days", b: "India's sun-drenched belt — including our Telangana cultivation zone — delivers the consistent direct sunlight Arthrospira metabolism peaks under, 10 months a year." },
  { icon: Droplets, t: "Naturally Alkaline Groundwater", b: "Local geology yields pH 9.5+ artesian water — biologically ideal for spirulina, with no chemical adjustment required." },
  { icon: FlaskConical, t: "Hyderabad Pharma Cluster", b: "Our site sits 160 km from one of the world's largest pharma capitals — direct access to GMP infrastructure, NABL labs, and skilled talent." },
  { icon: Sprout, t: "Adjacent Curcumin Heritage", b: "We chose the Nizamabad–Telangana corridor — India's curcumin heartland — making our Blue-Gold spirulina-phycocyanin-curcumin blend uniquely possible." },
];

const About = () => {
  return (
    <div data-testid="page-about" className="pt-[72px]">
      {/* HERO */}
      <section className="container-wide py-16 md:py-28">
        <p className="eyebrow mb-5">◦  About Spiruva</p>
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl text-brand-black tracking-tight leading-[1.02] max-w-5xl">
          Crafted in India. <span className="italic text-brand-teal">Preparing global supply for July 2027.</span>
        </h1>
        <p className="mt-6 text-brand-black/70 max-w-2xl leading-relaxed text-sm md:text-base">
          SPIRUVA is the export and brand-led platform of <strong className="text-brand-black">Carbofixers</strong> — the technical parent company providing the manufacturing depth, process engineering, and industrial credibility behind every grade in our portfolio. Science-backed brand. Industrial-strength foundation.
        </p>
        <div className="mt-8 bg-brand-cream/60 border-l-2 border-brand-gold p-5 md:p-6 max-w-3xl">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-gold mb-2">◦  Parent Company</p>
          <p className="text-sm md:text-base text-brand-black/80 leading-relaxed">
            <strong className="text-brand-black">Carbofixers</strong> is the manufacturing and process-engineering arm — bringing IIT-trained chemical engineering, continuous-process scale-up expertise, and pharma-cluster ecosystem access. SPIRUVA is how that depth reaches global brand owners, distributors, and institutional buyers.
          </p>
        </div>
      </section>

      {/* FOUNDERS */}
      <section className="bg-brand-cream border-y border-brand-green/15">
        <div className="container-wide py-24">
          <div className="max-w-3xl">
            <p className="eyebrow mb-4">◦  Founding Team</p>
            <h2 className="font-display text-4xl md:text-5xl text-brand-black leading-tight">
              Three disciplines. <span className="italic text-brand-teal">One mission.</span>
            </h2>
            <p className="text-brand-black/70 mt-5 leading-relaxed">
              Spiruva is built on a rare convergence of scientific expertise, commercial acumen, and operational discipline. Together, our founding team brings chemical engineering precision, healthcare market access, and agricultural mastery — covering every dimension of building a world-class spirulina and phycocyanin business from Nizamabad to the global market.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {FOUNDERS.map((f, i) => (
              <article
                key={f.name}
                data-testid={`founder-card-${f.initials}`}
                className="relative bg-white border border-brand-green/15 p-8 flex flex-col h-full hover:border-brand-gold/60 hover:shadow-[0_18px_40px_-15px_rgba(0,109,119,0.18)] transition-all duration-300 animate-fade-up"
                style={{ animationDelay: `${i * 120}ms` }}
              >
                {/* top accent bar */}
                <span
                  aria-hidden
                  className="absolute top-0 left-0 right-0 h-[3px]"
                  style={{ background: `linear-gradient(90deg, ${f.accent}, ${f.accent}80, transparent)` }}
                />

                <div className="flex items-start justify-between gap-4">
                  <div className={`w-16 h-16 flex items-center justify-center font-display text-2xl tracking-tight ${f.initialBg} border border-current/20`}>
                    {f.initials}
                  </div>
                  <f.icon size={20} className="text-brand-black/30" />
                </div>

                <span className={`mt-6 self-start inline-flex items-center font-mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border ${f.accentClass}`}>
                  {f.role}
                </span>

                <h3 className="font-display text-2xl mt-4 text-brand-black leading-tight">{f.name}</h3>
                <p className="text-sm text-brand-black/55 mt-1">{f.title}</p>

                <div className="flex flex-wrap gap-1.5 mt-5">
                  {f.chips.map((c) => (
                    <span key={c} className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-brand-cream border border-brand-green/15 text-brand-black/70">
                      {c}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-brand-black/70 mt-6 leading-relaxed">
                  {f.bio}
                </p>
              </article>
            ))}
          </div>

          <p className="text-center font-display italic text-xl md:text-2xl text-brand-black/80 mt-14 max-w-3xl mx-auto">
            Commerce × Science × Operations — India's most complete spirulina founding team.
          </p>
        </div>
      </section>

      {/* VISION */}
      <section className="container-wide py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <p className="eyebrow mb-4">◦  Vision · 2030</p>
          <h2 className="font-display text-3xl md:text-4xl text-brand-black leading-tight">
            India's premier <span className="italic text-brand-teal">export-grade phycocyanin</span> producer.
          </h2>
        </div>
        <div className="md:col-span-7 space-y-5 text-brand-black/75 leading-relaxed">
          <p>
            Our 2030 ambition is straightforward: supply 1 in 4 EU and US nutraceutical brands with India-grown phycocyanin — and prove that the global premium-blue market doesn't have to depend on Chinese-origin biomass or European-produced extracts.
          </p>
          <p>
            We're scaling cultivation, expanding our pharmaceutical (E30) line, and building exclusive distribution partnerships across the FTA-corridor markets where India's trade position gives Spiruva customers a structural cost advantage.
          </p>
        </div>
      </section>

      {/* WHY INDIA (Nizamabad folded in) */}
      <section className="bg-brand-black text-brand-cream relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="container-wide py-24 relative">
          <p className="eyebrow-light mb-4">◦  Why India</p>
          <h2 className="font-display text-4xl md:text-5xl leading-tight max-w-3xl">
            The terroir of <span className="italic text-brand-gold">premium spirulina.</span>
          </h2>
          <p className="text-brand-cream/75 mt-6 max-w-3xl leading-relaxed">
            India offers a rare combination for industrial-scale spirulina cultivation: 300+ days of high-UV sunlight, naturally alkaline groundwater, world-class pharma infrastructure, and the world's deepest curcumin heritage. We chose the Nizamabad–Telangana corridor specifically — alkaline aquifers, proximity to Hyderabad's pharma cluster, established turmeric trade routes, and direct logistics access to Chennai and Mumbai export ports — to operate at the intersection of all four.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-px mt-14 bg-brand-cream/10">
            {WHY_INDIA.map((x, i) => (
              <Reveal key={i} delay={i * 110} data-testid={`why-india-${i}`} className="bg-brand-black p-8 group">
                <x.icon size={20} className="text-brand-gold group-hover:scale-110 transition-transform duration-300" />
                <h3 className="font-display text-2xl mt-5">{x.t}</h3>
                <p className="text-sm text-brand-cream/70 mt-3 leading-relaxed">{x.b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FACILITY */}
      <section className="container-wide py-24 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <Reveal className="relative aspect-video w-full overflow-hidden border border-brand-green/15 group">
          {/* Brand duotone layer: deep teal multiply + gold highlight overlay makes the photo feel curated, not stock */}
          <img
            src={FARM_IMG}
            alt="Spiruva pond aerial view"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-[1.04]"
            style={{ filter: "saturate(0.55) contrast(1.05) brightness(0.92)" }}
          />
          <div
            className="absolute inset-0 mix-blend-multiply"
            style={{ background: "linear-gradient(135deg, rgba(0,109,119,0.45) 0%, rgba(26,77,46,0.55) 100%)" }}
            aria-hidden
          />
          <div
            className="absolute inset-0 mix-blend-screen opacity-40"
            style={{ background: "radial-gradient(circle at 70% 25%, rgba(201,168,76,0.5) 0%, transparent 55%)" }}
            aria-hidden
          />
          <div className="absolute inset-0 grain opacity-30 mix-blend-overlay" aria-hidden />

          {/* Caption / corner badges */}
          <div className="absolute top-4 left-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-cream">
            <span className="w-1.5 h-1.5 bg-brand-gold animate-pulse" />
            Cultivation Zone · Telangana
          </div>
          <div className="absolute bottom-4 right-4 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-cream/85">
            17.45°N 78.81°E
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(90deg, #006d77, #c9a84c, transparent)" }} />
        </Reveal>
        <div>
          <p className="eyebrow mb-4">◦  Facility</p>
          <h2 className="font-display text-4xl md:text-5xl text-brand-black leading-tight">
            22 acres. <span className="italic text-brand-teal">Built for export from day one.</span>
          </h2>
          <p className="text-brand-black/70 mt-6 leading-relaxed max-w-lg">
            Our Telangana facility integrates open-pond cultivation, in-house cell disruption, ultrafiltration, freeze-drying, and a NABL-grade analytical lab — all under one compliance umbrella. Drone surveillance covers every pond. Every. Single. Pond.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-px bg-brand-green/15 border border-brand-green/15">
            {[
              { v: "22 ac", l: "Cultivation" },
              { v: "120 MT", l: "Annual capacity" },
              { v: "<24h", l: "Pond to lyophiliser" },
            ].map((x, i) => (
              <Reveal key={x.l} delay={i * 100} className="bg-white p-5">
                <p className="font-display text-2xl text-brand-teal">{x.v}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/50 mt-1">{x.l}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-cream border-y border-brand-green/15">
        <div className="container-wide py-24 text-center">
          <p className="eyebrow mb-4">◦  Partner</p>
          <h2 className="font-display text-4xl md:text-5xl text-brand-black max-w-3xl mx-auto leading-tight">
            Procurement teams, formulators, and distributors — <span className="italic text-brand-teal">we're listening.</span>
          </h2>
          <Link to="/inquiry" data-testid="about-cta-inquiry" className="btn-primary mt-10">
            Open a B2B Conversation <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
